import type { AudioManager } from "../core/AudioManager";
import type { HeadlampController } from "../player/HeadlampController";

export const HEADLAMP_BLACKOUT_SECONDS = 2;
export const HEADLAMP_RECOVERY_SECONDS = 1;
export const INTIMIDATION_DURATION_SECONDS = 4;
export const AMBIENCE_DUCK_LEVEL = 0.32;
// Multiplies the normal 17% dark-jungle baseline. Effective general light at
// the blackout floor is therefore 1.7% of the original configured intensity.
export const JUNGLE_BLACKOUT_LIGHT_MULTIPLIER = 0.1;

export function headlampFlickerMultiplier(elapsed:number,reducedFlash=false):number{
  if(elapsed<0||elapsed>=HEADLAMP_BLACKOUT_SECONDS+HEADLAMP_RECOVERY_SECONDS)return 1;
  if(elapsed<HEADLAMP_BLACKOUT_SECONDS)return 0;
  const recovery=elapsed-HEADLAMP_BLACKOUT_SECONDS;
  if(reducedFlash)return Math.min(1,recovery/HEADLAMP_RECOVERY_SECONDS);
  const samples:readonly [number,number][]=[[0,0],[.15,.6],[.3,.1],[.5,.8],[.65,.2],[.9,1],[1,1]];
  for(let index=1;index<samples.length;index++){
    const previous=samples[index-1]!,next=samples[index]!;
    if(recovery<=next[0]){const t=(recovery-previous[0])/Math.max(.001,next[0]-previous[0]);return previous[1]+(next[1]-previous[1])*Math.max(0,Math.min(1,t));}
  }
  return 1;
}

export function ambienceDuckMultiplier(elapsed:number):number{
  if(elapsed<0||elapsed>=INTIMIDATION_DURATION_SECONDS)return 1;
  if(elapsed<2.45)return AMBIENCE_DUCK_LEVEL;
  const t=Math.min(1,(elapsed-2.45)/(INTIMIDATION_DURATION_SECONDS-2.45));
  return AMBIENCE_DUCK_LEVEL+(1-AMBIENCE_DUCK_LEVEL)*(t*t*(3-2*t));
}

export function jungleLightingMultiplier(elapsed:number):number{
  if(elapsed<0||elapsed>=HEADLAMP_BLACKOUT_SECONDS+HEADLAMP_RECOVERY_SECONDS)return 1;
  if(elapsed<HEADLAMP_BLACKOUT_SECONDS)return JUNGLE_BLACKOUT_LIGHT_MULTIPLIER;
  const t=Math.max(0,Math.min(1,(elapsed-HEADLAMP_BLACKOUT_SECONDS)/HEADLAMP_RECOVERY_SECONDS));
  const smooth=t*t*(3-2*t);
  return JUNGLE_BLACKOUT_LIGHT_MULTIPLIER+(1-JUNGLE_BLACKOUT_LIGHT_MULTIPLIER)*smooth;
}

/** One game-clock-owned effect. Calling trigger again refreshes it instead of stacking. */
export class IntimidationEffectManager{
  private elapsed=INTIMIDATION_DURATION_SECONDS;
  private active=false;
  private readonly reducedFlash=typeof matchMedia!=="undefined"&&matchMedia("(prefers-reduced-motion: reduce)").matches;
  constructor(private readonly headlamp:HeadlampController,private readonly audio:AudioManager,private readonly setJungleLightingMultiplier:(multiplier:number)=>void=()=>{}){}
  trigger():void{this.elapsed=0;this.active=true;this.apply();}
  update(deltaSeconds:number):void{
    if(!this.active)return;
    this.elapsed=Math.min(INTIMIDATION_DURATION_SECONDS,this.elapsed+Math.max(0,deltaSeconds));
    this.apply();
    if(this.elapsed>=INTIMIDATION_DURATION_SECONDS)this.stop();
  }
  stop():void{this.active=false;this.elapsed=INTIMIDATION_DURATION_SECONDS;this.headlamp.setIntensityMultiplier(1);this.audio.setAmbienceDuckMultiplier(1);this.setJungleLightingMultiplier(1);}
  dispose():void{this.stop();}
  get isActive():boolean{return this.active;}
  get elapsedSeconds():number{return this.elapsed;}
  get phase():"idle"|"blackout"|"recovery"|"ambience-restore"{if(!this.active)return"idle";if(this.elapsed<HEADLAMP_BLACKOUT_SECONDS)return"blackout";if(this.elapsed<HEADLAMP_BLACKOUT_SECONDS+HEADLAMP_RECOVERY_SECONDS)return"recovery";return"ambience-restore";}
  get remainingSeconds():number{return this.active?Math.max(0,INTIMIDATION_DURATION_SECONDS-this.elapsed):0;}
  get recoveryProgress():number{return Math.max(0,Math.min(1,(this.elapsed-HEADLAMP_BLACKOUT_SECONDS)/HEADLAMP_RECOVERY_SECONDS));}
  private apply():void{this.headlamp.setIntensityMultiplier(headlampFlickerMultiplier(this.elapsed,this.reducedFlash));this.audio.setAmbienceDuckMultiplier(ambienceDuckMultiplier(this.elapsed));this.setJungleLightingMultiplier(jungleLightingMultiplier(this.elapsed));}
}
